import React, { useEffect, useState } from 'react';
import {
    VStack,
    HStack,
    Image,
    FlatList,
    Pressable,
    ChevronLeftIcon,
    Box,
    ScrollView,
    Center,
    Icon,
    View
} from 'native-base';
import { Caption, H1 } from '~/shared/Typography';
import {
    useAddItemToCartMutation,
    useCreateCheckoutMutation,
    useDutchieStore,
    useI18n
} from '@springbig/shared';
import Section from '~/shared/Section';
import { useShopStyles } from '~/shared/styles';
import { SBDropdown } from '~/shared/SBDropdown';
import { QuantityPicker } from '~/shared/QuantityPicker';
import { ProductImage, ProductVariant } from '@springbig/shared/src/services/dutchie/schema-types';
import { VariantCard } from '~/pages/shop/components/VariantCard';
import { useAppNavigate } from '~/pages';
import { Cannabinoid } from '~/pages/shop/components/Cannabinoid';
import { useToast } from '~/hooks/toast';
import { faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import { SpIcon } from '~/shared/Icon';

export const ProductDetailsPage = () => {

    const {
        section_background,
        text,
        button_background,
        button_text,
        icon
    } = useShopStyles();

    const { navigate, params } = useAppNavigate();
    const { menuType, product, checkout, fullfillmentOption, retailer, setCheckout, cartItemsAmount } = useDutchieStore();

    const [selectedVariant, setSelectedVariant] = useState<ProductVariant>(product?.variants[0]);
    const [selectedQuantity, setSelectedQuantity] = useState<number>(1);
    const { mutate: addItem, data: newAddItem, isLoading: addItemLoading } = useAddItemToCartMutation();
    const { mutate: createCheckout, data: newCheckout, isLoading: checkoutLoading } = useCreateCheckoutMutation();
    const { errorToast } = useToast();
    const { t } = useI18n();

    useEffect(() => {

        if (newCheckout) {
            setCheckout(newCheckout.createCheckout);
            addItem({
                retailerId: retailer.id,
                checkoutId: newCheckout.createCheckout.id,
                quantity: selectedQuantity,
                option: selectedVariant.option,
                productId: product.id,
            }, {
                onError: (error) => {

                    errorToast(error.data.errors[0].message);
                }
            });
        }
    }, [newCheckout]);

    useEffect(() => {

        if (newAddItem) {
            setCheckout(newAddItem.addItem);
        }
    }, [newAddItem]);

    const isDiscounted = (): boolean => {

        let state = false;
        if (menuType === 'RECREATIONAL') {
            state = !!selectedVariant.specialPriceRec;
        }

        if (menuType === 'MEDICAL') {
            state = !!selectedVariant.specialPriceMed;
        }

        return state;
    };

    const displayFinalPrice = (variant: ProductVariant): string => {

        let finalPrice;
        if (menuType === 'RECREATIONAL') {
            finalPrice = variant.specialPriceRec ? variant.specialPriceRec : variant.priceRec;
        }

        if (menuType === 'MEDICAL') {
            finalPrice = variant.specialPriceMed ? variant.specialPriceMed : variant.priceMed;
        }

        return `$${(finalPrice * selectedQuantity).toFixed(2)}`;
    };

    const displayRawPrice = (variant: ProductVariant): string => {

        let finalPrice;
        if (menuType === 'RECREATIONAL') {
            finalPrice = variant.priceRec;
        }

        if (menuType === 'MEDICAL') {
            finalPrice = variant.priceMed;
        }

        return `$${(finalPrice * selectedQuantity).toFixed(2)}`;
    };

    return ( product ?
        <Section h={'100%'} w={'clamp(320px, 100vw, 480px)'} overflowX={'hidden'}>
            <VStack space={1} justifyContent={'space-between'} h={'100%'}>
                <HStack alignItems={'center'} justifyContent={'space-between'} space={4} p={2}>
                    <Pressable
                        onPress={() => navigate(`w/${params.wallet_id}/shop/`)}
                        bg={section_background}
                        p={1}
                        rounded={'md'}
                    >
                        <ChevronLeftIcon size='lg' color={button_background} />
                    </Pressable>
                    <VStack flex={1}>
                        <H1 fontSize={14} my={0} textAlign={'center'}>
                            { product.name }
                        </H1>
                        <Caption textAlign={'center'}>
                            { product.brand.name }
                        </Caption>
                    </VStack>
                    <Pressable
                        rounded={'50%'}
                        p={4}
                        bg={section_background}
                        onPress={() => {

                            navigate(`w/${params.wallet_id}/shop/cart`);
                        }}
                    >
                        <View style={{ position: 'relative', display: 'inline-block' }}>
                            <SpIcon color={icon} icon={faShoppingCart} />
                            {cartItemsAmount > 0 && (
                                <Box
                                    style={{
                                        position: 'absolute',
                                        backgroundColor: 'red',
                                        color: 'white',
                                        borderRadius: '50%',
                                        fontSize: '12px',
                                        top: '-10px',
                                        right: '-20px',
                                        width: '20px',
                                        textAlign: 'center'
                                    }}
                                >
                                    <Caption color="white" fontSize="12px">
                                        {cartItemsAmount}
                                    </Caption>
                                </Box>
                            )}
                        </View>
                    </Pressable>
                </HStack>
                <ScrollView>
                    <VStack
                        space={4}
                        px={2}
                    >
                        {product.images.length  === 1 ?
                            <Image
                                src={product.images[0].url + '?w=450&h=450'}
                                rounded={'3xl'}
                                w={'clamp(320px, 93vw, 450px)'}
                                h={'clamp(320px, 93vw, 450px)'}
                            /> :
                            <FlatList
                                py={2}
                                horizontal={true}
                                data={product.images}
                                renderItem={( { item, index }: { item: ProductImage, index: number}) =>

                                    <Image
                                        key={index}
                                        src={item.url + '?w=450&h=450'}
                                        rounded={'3xl'}
                                        mr={product.images.length - 1 !== index ? 4 : 0}
                                        w={'450px'}
                                        h={'450px'}
                                    />
                                }
                            />
                        }
                        <Caption textAlign={'center'}>
                            { product.description }
                        </Caption>
                    </VStack>

                    <VStack space={4} px={2} py={4}>
                        { product.effects && product.effects.length > 0 &&
                            <SBDropdown
                                title={'effects'}>
                                <HStack
                                    bg={section_background}
                                    flexWrap={'wrap'}
                                    justifyContent={'space-around'}
                                    space={4}
                                    rounded={'sm'}
                                    p={2}
                                >
                                    { product.effects.map((effect, index) =>

                                        <Caption color={text} key={index}>
                                            { effect.toLowerCase().replace('_', ' ') }
                                        </Caption>
                                    )}
                                </HStack>
                            </SBDropdown>
                        }
                        { product.cannabinoids?.length > 0 &&
                            <SBDropdown
                                title={'cannabinoids'}>
                                <VStack
                                    bg={section_background}
                                    space={2}
                                    rounded={'sm'}
                                    p={2}
                                >
                                    { product.cannabinoids.map((type, index) =>

                                        <Cannabinoid type={type} key={index} />
                                    )}
                                </VStack>
                            </SBDropdown>
                        }
                    </VStack>

                    { product.variants.length > 1 &&
                        <VStack space={4} px={2}>
                            <FlatList
                                py={4}
                                horizontal={true}
                                data={product.variants}
                                keyExtractor={(variant: ProductVariant, index: number) => `${variant.id}-${index}`}
                                renderItem={({ item, index }: { item: ProductVariant; index: number }) =>

                                    <>
                                        { menuType === 'RECREATIONAL' ?
                                            <VariantCard
                                                key={index}
                                                id={item.id}
                                                price={item.priceRec}
                                                specialPrice={item.specialPriceRec}
                                                name={item.option}
                                                isSelected={item.id === selectedVariant.id}
                                                onPress={() => setSelectedVariant(item)}
                                            />
                                            :
                                            <VariantCard
                                                key={index}
                                                id={item.id}
                                                price={item.priceMed}
                                                specialPrice={item.specialPriceMed}
                                                name={item.option}
                                                isSelected={item.id === selectedVariant.id}
                                                onPress={() => setSelectedVariant(item)}
                                            />
                                        }
                                    </>
                                }
                            />
                        </VStack>
                    }

                    <VStack space={4} px={2} py={4} justifyContent={'center'}>
                        { selectedVariant.quantity > 1 &&
                            <QuantityPicker
                                isNullable={false}
                                quantity={selectedQuantity}
                                maxQuantity={selectedVariant.quantity}
                                setQuantity={(quantity) => setSelectedQuantity(quantity)}
                                isLoading={false}
                                variant={'large'}
                            />
                        }

                        <Pressable
                            position={'relative'}
                            disabled={addItemLoading || checkoutLoading}
                            opacity={addItemLoading || checkoutLoading ? .5 : 1}
                            onPress={() => {

                                if (checkout) {
                                    addItem({
                                        retailerId: retailer.id,
                                        checkoutId: checkout.id,
                                        quantity: selectedQuantity,
                                        option: selectedVariant.option,
                                        productId: product.id,
                                    }, {
                                        onError: (error) => {

                                            errorToast(error.data.errors[0].message);
                                        }
                                    });
                                }
                                else {
                                    createCheckout({
                                        pricingType: menuType,
                                        orderType: fullfillmentOption.key,
                                        retailerId: retailer.id,
                                    });
                                }
                            }}
                        >
                            <HStack>
                                <VStack px={4} py={2} alignItems={'center'} justifyContent={'center'}>
                                    <Box
                                        position={'absolute'}
                                        borderColor={button_background}
                                        bg={section_background}
                                        borderWidth={1}
                                        borderRightWidth={0}
                                        roundedLeft={'2xl'}
                                        h={16}
                                        w={'150%'}
                                        left={0}
                                        top={0}
                                        zIndex={-1}
                                    ></Box>
                                    {/*{ isDiscounted() &&*/}
                                    {/*    <Caption*/}
                                    {/*        textAlign={'center'}*/}
                                    {/*        fontSize={14}*/}
                                    {/*        color={text}*/}
                                    {/*        opacity={.4}*/}
                                    {/*        style={{ textDecorationLine: 'line-through' }}*/}
                                    {/*    >*/}
                                    {/*        { displayRawPrice(selectedVariant) }*/}
                                    {/*    </Caption>*/}
                                    {/*}*/}
                                    <Caption
                                        textAlign={'center'}
                                        fontSize={16}
                                        color={text}
                                    >
                                        { displayFinalPrice(selectedVariant) }
                                    </Caption>
                                </VStack>
                                <HStack
                                    bg={button_background}
                                    rounded={'2xl'}
                                    py={4}
                                    px={8}
                                    h={16}
                                    flex={1}
                                    alignItems={'center'}
                                    isDisabled={!fullfillmentOption?.isOpen}
                                >
                                    <Caption
                                        color={button_text}
                                        mx={'auto'}
                                        fontSize={16}
                                    >
                                        { t('shop.add_to_cart') }
                                    </Caption>
                                </HStack>
                            </HStack>
                        </Pressable>
                    </VStack>
                </ScrollView>
            </VStack>
        </Section>
        :
        <></>
    );
};
